import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Baby, GraduationCap, Bell, Loader2, BookOpen,
  Gamepad2, ClipboardList, MessageSquare, Receipt,
  CalendarCheck, CalendarOff, Brain, Send, Eye, Calendar,
} from "lucide-react";
import ParentMessaging from "@/components/parent/ParentMessaging";
import ParentInvoices from "@/components/parent/ParentInvoices";
import ParentAttendance from "@/components/parent/ParentAttendance";
import ParentSchedule from "@/components/parent/ParentSchedule";
import ParentWeeklySchedule from "@/components/parent/ParentWeeklySchedule";
import ParentLeaveRequests from "@/components/parent/ParentLeaveRequests";
import ParentAssignments from "@/components/parent/ParentAssignments";
import ParentChildReport from "@/components/parent/ParentChildReport";
import ParentDailySummary from "@/components/parent/ParentDailySummary";
import ParentChildCards from "@/components/parent/ParentChildCards";
import ParentChildStats from "@/components/parent/ParentChildStats";
import ParentExamResults from "@/components/parent/ParentExamResults";
import ParentGameResults from "@/components/parent/ParentGameResults";
import ParentOverview from "@/components/parent/ParentOverview";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { subDays, startOfDay, format } from "date-fns";
import { ar } from "date-fns/locale";
import { motion } from "framer-motion";
import { toast } from "sonner";

const ParentDashboard = () => {
  const { user, profile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [children, setChildren] = useState<any[]>([]);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [examResults, setExamResults] = useState<any[]>([]);
  const [gameResults, setGameResults] = useState<any[]>([]);
  const [selectedChild, setSelectedChild] = useState<string | null>(null);
  const [todayAttendance, setTodayAttendance] = useState<any[]>([]);
  const [pendingAssignments, setPendingAssignments] = useState<any[]>([]);
  const [upcomingExams, setUpcomingExams] = useState<any[]>([]);
  const [latestStars, setLatestStars] = useState<any | null>(null);
  const [weeklyData, setWeeklyData] = useState<any[]>([]);
  const [todayLessonsCount, setTodayLessonsCount] = useState(0);
  const [overdueInvoices, setOverdueInvoices] = useState<any[]>([]);
  const [quickMsgOpen, setQuickMsgOpen] = useState(false);
  const [quickMsg, setQuickMsg] = useState("");
  const [quickMsgType, setQuickMsgType] = useState("inquiry");
  const [sendingMsg, setSendingMsg] = useState(false);
  // New state
  const [todayActivities, setTodayActivities] = useState(0);
  const [childMood, setChildMood] = useState<{ sentiment: string; notes: string | null } | null>(null);
  const [weeklyComparison, setWeeklyComparison] = useState<{ current: number; previous: number } | null>(null);
  const [childObservations, setChildObservations] = useState<Record<string, { notes: string | null; sentiment: string } | undefined>>({});
  const [childActivityProgress, setChildActivityProgress] = useState<Record<string, number>>({});
  const [latestMessage, setLatestMessage] = useState<any | null>(null);
  const [notifFilter, setNotifFilter] = useState<string>("all");

  useEffect(() => {
    if (user) fetchData();
  }, [user]);

  const fetchData = async () => {
    if (!user) return;
    setLoading(true);

    const { data: childrenData } = await supabase
      .from("children")
      .select("*, classes(name)")
      .eq("parent_id", user.id)
      .order("full_name");

    const kids = childrenData || [];
    setChildren(kids);

    if (kids.length > 0) {
      setSelectedChild(kids[0].id);
      const childIds = kids.map((c) => c.id);
      const today = new Date().toISOString().split("T")[0];
      const classIds = kids.map((c: any) => c.class_id).filter(Boolean);

      const [examsRes, gamesRes, notifRes, attendanceRes, assignmentsRes, upcomingExamsRes, lessonsRes, invoicesRes, activitiesRes, observationsRes, messagesRes] = await Promise.all([
        supabase.from("exam_submissions").select("*, exams(title, scheduled_at, class_id)").in("child_id", childIds).order("created_at", { ascending: false }).limit(50),
        supabase.from("game_results").select("*").in("child_id", childIds).order("played_at", { ascending: false }).limit(50),
        supabase.from("notifications").select("*").eq("user_id", user.id).order("created_at", { ascending: false }).limit(30),
        supabase.from("attendance").select("*").in("attendee_id", childIds).eq("date", today).eq("attendee_type", "child"),
        supabase.from("assignments").select("*, assignment_submissions(status, child_id)").in("class_id", classIds).gte("due_date", new Date().toISOString()).order("due_date"),
        supabase.from("exams").select("*").in("class_id", classIds).gte("scheduled_at", new Date().toISOString()).order("scheduled_at").limit(5),
        supabase.from("lessons").select("id").in("class_id", classIds).gte("scheduled_at", today + "T00:00:00").lte("scheduled_at", today + "T23:59:59"),
        supabase.from("invoices").select("id, title, amount, due_date, child_id").in("child_id", childIds).eq("status", "pending").lt("due_date", today),
        // Today's complementary activities
        supabase.from("child_activities").select("id, child_id").in("child_id", childIds).gte("completed_at", today + "T00:00:00").lte("completed_at", today + "T23:59:59"),
        // Latest observations per child
        supabase.from("child_observations").select("child_id, sentiment, notes, observation_date").in("child_id", childIds).order("observation_date", { ascending: false }).limit(kids.length * 2),
        // Latest message
        supabase.from("messages").select("content, created_at").or(`sender_id.eq.${user.id},receiver_id.eq.${user.id}`).order("created_at", { ascending: false }).limit(1),
      ]);

      setExamResults(examsRes.data || []);
      setGameResults(gamesRes.data || []);
      setNotifications(notifRes.data || []);
      setTodayAttendance(attendanceRes.data || []);
      setUpcomingExams(upcomingExamsRes.data || []);
      setTodayLessonsCount(lessonsRes.data?.length || 0);
      setOverdueInvoices(invoicesRes.data || []);
      setTodayActivities((activitiesRes.data || []).length);
      setLatestMessage((messagesRes.data || [])[0] || null);

      // Child observations map
      const obsMap: Record<string, { notes: string | null; sentiment: string }> = {};
      (observationsRes.data || []).forEach((o: any) => {
        if (!obsMap[o.child_id]) obsMap[o.child_id] = { notes: o.notes, sentiment: o.sentiment };
      });
      setChildObservations(obsMap);

      // First child mood
      const firstChildObs = obsMap[kids[0].id];
      setChildMood(firstChildObs || null);

      // Activity progress per child (today count / 3 goal * 100)
      const actMap: Record<string, number> = {};
      (activitiesRes.data || []).forEach((a: any) => {
        actMap[a.child_id] = (actMap[a.child_id] || 0) + 1;
      });
      const progressMap: Record<string, number> = {};
      Object.keys(actMap).forEach((cid) => {
        progressMap[cid] = Math.min((actMap[cid] / 3) * 100, 100);
      });
      setChildActivityProgress(progressMap);

      const allAssignments = assignmentsRes.data || [];
      const pending = allAssignments.filter((a: any) => {
        const subs = a.assignment_submissions || [];
        return !childIds.some((cid: string) => subs.some((s: any) => s.child_id === cid && s.status !== "pending"));
      });
      setPendingAssignments(pending);

      const starred = (gamesRes.data || []).find((g: any) => (g.stars || 0) >= 3);
      setLatestStars(starred || null);

      // Weekly performance
      const weekDays = Array.from({ length: 7 }, (_, i) => {
        const d = subDays(new Date(), 6 - i);
        return { date: d, label: format(d, "EEE", { locale: ar }), dayStart: startOfDay(d) };
      });
      const allGames = gamesRes.data || [];
      const allExamSubs = examsRes.data || [];
      const wData = weekDays.map((wd) => {
        const nextDay = new Date(wd.dayStart);
        nextDay.setDate(nextDay.getDate() + 1);
        const dayGames = allGames.filter((g: any) => { const d = new Date(g.played_at); return d >= wd.dayStart && d < nextDay && childIds.includes(g.child_id); });
        const dayExams = allExamSubs.filter((e: any) => { if (!e.submitted_at) return false; const d = new Date(e.submitted_at); return d >= wd.dayStart && d < nextDay && childIds.includes(e.child_id); });
        const scores = [...dayGames.map((g: any) => g.percentage || 0), ...dayExams.map((e: any) => e.percentage || 0)];
        const avg = scores.length > 0 ? Math.round(scores.reduce((a: number, b: number) => a + b, 0) / scores.length) : null;
        return { day: wd.label, score: avg, activities: scores.length };
      });
      setWeeklyData(wData);

      // Weekly comparison
      const thisWeekScores = wData.filter((d) => d.score !== null).map((d) => d.score as number);
      const currentAvg = thisWeekScores.length > 0 ? Math.round(thisWeekScores.reduce((a, b) => a + b, 0) / thisWeekScores.length) : 0;
      // Previous week simple estimate from older games
      const prevWeekGames = allGames.filter((g: any) => {
        const d = new Date(g.played_at);
        return d >= subDays(new Date(), 13) && d < subDays(new Date(), 6);
      });
      const prevScores = prevWeekGames.map((g: any) => g.percentage || 0);
      const prevAvg = prevScores.length > 0 ? Math.round(prevScores.reduce((a: number, b: number) => a + b, 0) / prevScores.length) : 0;
      if (currentAvg > 0 || prevAvg > 0) {
        setWeeklyComparison({ current: currentAvg, previous: prevAvg });
      }
    } else {
      const { data: notifData } = await supabase.from("notifications").select("*").eq("user_id", user.id).order("created_at", { ascending: false }).limit(30);
      setNotifications(notifData || []);
    }

    setLoading(false);
  };

  const markAsRead = async (id: string) => {
    await supabase.from("notifications").update({ is_read: true }).eq("id", id);
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
  };

  const markAllAsRead = async () => {
    const unreadIds = notifications.filter((n) => !n.is_read).map((n) => n.id);
    if (unreadIds.length === 0) return;
    await supabase.from("notifications").update({ is_read: true }).in("id", unreadIds);
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    toast.success("تم تحديد الكل كمقروء");
  };

  const msgTypeLabels: Record<string, string> = {
    inquiry: "استفسار",
    complaint: "شكوى",
    leave: "طلب إجازة",
  };

  const sendQuickMessage = async () => {
    if (!quickMsg.trim() || !user || !profile?.nursery_id) return;
    setSendingMsg(true);
    const prefix = `[${msgTypeLabels[quickMsgType]}] `;
    
    // Fetch nursery owner as receiver
    const { data: nurseryData } = await supabase
      .from("nurseries")
      .select("owner_id")
      .eq("id", profile.nursery_id)
      .single();
    
    const { error } = await supabase.from("messages").insert({
      content: prefix + quickMsg.trim(),
      sender_id: user.id,
      receiver_id: nurseryData?.owner_id || null,
      nursery_id: profile.nursery_id,
    });
    setSendingMsg(false);
    if (error) {
      toast.error("فشل إرسال الرسالة");
    } else {
      toast.success("تم إرسال الرسالة للإدارة");
      setQuickMsg("");
      setQuickMsgOpen(false);
    }
  };

  const childExams = examResults.filter((e) => e.child_id === selectedChild);
  const childGames = gameResults.filter((g) => g.child_id === selectedChild);
  const unreadCount = notifications.filter((n) => !n.is_read).length;
  const selectedChildData = children.find((c) => c.id === selectedChild);

  // Filter notifications
  const typeMap: Record<string, string[]> = {
    educational: ["exam", "lesson", "assignment", "game"],
    administrative: ["attendance", "leave", "general"],
    financial: ["invoice", "payment", "billing"],
  };
  const filteredNotifications = notifFilter === "all"
    ? notifications
    : notifications.filter((n) => (typeMap[notifFilter] || []).some((t) => n.type?.includes(t)));

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (children.length === 0) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-foreground">لوحة ولي الأمر</h1>
        <Card className="shadow-card">
          <CardContent className="py-16 text-center">
            <Baby className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">لا يوجد أطفال مرتبطين بحسابك</h3>
            <p className="text-muted-foreground text-sm">يرجى التواصل مع إدارة الحضانة لربط أطفالك بحسابك</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">مرحباً {profile?.full_name?.split(" ")[0] || "ولي الأمر"} 👋</h1>
        <p className="text-muted-foreground text-sm mt-1">متابعة تقدم أطفالك وإشعاراتك</p>
      </div>

      <ParentDailySummary
        children={children}
        todayAttendance={todayAttendance}
        todayLessonsCount={todayLessonsCount}
        pendingAssignments={pendingAssignments}
        upcomingExams={upcomingExams}
        overdueInvoices={overdueInvoices}
        latestStars={latestStars}
        weeklyData={weeklyData}
        todayActivities={todayActivities}
        childMood={childMood}
        weeklyComparison={weeklyComparison}
      />

      <ParentChildCards
        children={children}
        selectedChild={selectedChild}
        onSelectChild={setSelectedChild}
        todayAttendance={todayAttendance}
        examResults={examResults}
        gameResults={gameResults}
        childObservations={childObservations}
        childActivityProgress={childActivityProgress}
      />

      {selectedChildData && (
        <ParentChildStats childExams={childExams} childGames={childGames} />
      )}

      {/* Grouped Tabs */}
      <Tabs defaultValue="overview" dir="rtl">
        <ScrollArea className="w-full" dir="rtl">
          <TabsList className="inline-flex h-auto gap-1 p-1 w-max min-w-full">
            <TabsTrigger value="overview" className="gap-1 text-xs sm:text-sm">
              <Eye className="w-4 h-4" />
              <span className="hidden sm:inline">نظرة عامة</span>
              <span className="sm:hidden">عامة</span>
            </TabsTrigger>

            <div className="w-px h-6 bg-border mx-1 self-center" />

            <TabsTrigger value="exams" className="gap-1 text-xs sm:text-sm">
              <GraduationCap className="w-4 h-4" />
              <span className="hidden sm:inline">الامتحانات</span>
              <span className="sm:hidden">امتحانات</span>
            </TabsTrigger>
            <TabsTrigger value="assignments" className="gap-1 text-xs sm:text-sm">
              <ClipboardList className="w-4 h-4" />
              <span className="hidden sm:inline">الواجبات</span>
              <span className="sm:hidden">واجبات</span>
            </TabsTrigger>
            <TabsTrigger value="schedule" className="gap-1 text-xs sm:text-sm">
              <BookOpen className="w-4 h-4" />
              <span className="hidden sm:inline">الدروس</span>
              <span className="sm:hidden">دروس</span>
            </TabsTrigger>
            <TabsTrigger value="weekly-schedule" className="gap-1 text-xs sm:text-sm">
              <Calendar className="w-4 h-4" />
              <span className="hidden sm:inline">الجدول الأسبوعي</span>
              <span className="sm:hidden">جدول</span>
            </TabsTrigger>
            <TabsTrigger value="games" className="gap-1 text-xs sm:text-sm">
              <Gamepad2 className="w-4 h-4" />
              <span className="hidden sm:inline">الألعاب</span>
              <span className="sm:hidden">ألعاب</span>
            </TabsTrigger>

            <div className="w-px h-6 bg-border mx-1 self-center" />

            <TabsTrigger value="attendance" className="gap-1 text-xs sm:text-sm">
              <CalendarCheck className="w-4 h-4" />
              <span className="hidden sm:inline">الحضور</span>
              <span className="sm:hidden">حضور</span>
            </TabsTrigger>
            <TabsTrigger value="leaves" className="gap-1 text-xs sm:text-sm">
              <CalendarOff className="w-4 h-4" />
              <span className="hidden sm:inline">الإجازات</span>
              <span className="sm:hidden">إجازات</span>
            </TabsTrigger>
            <TabsTrigger value="invoices" className="gap-1 text-xs sm:text-sm">
              <Receipt className="w-4 h-4" />
              <span className="hidden sm:inline">الفواتير</span>
              <span className="sm:hidden">فواتير</span>
            </TabsTrigger>

            <div className="w-px h-6 bg-border mx-1 self-center" />

            <TabsTrigger value="child-report" className="gap-1 text-xs sm:text-sm">
              <Brain className="w-4 h-4" />
              <span className="hidden sm:inline">التقرير الشامل</span>
              <span className="sm:hidden">تقرير</span>
            </TabsTrigger>

            <div className="w-px h-6 bg-border mx-1 self-center" />

            <TabsTrigger value="messages" className="gap-1 text-xs sm:text-sm">
              <MessageSquare className="w-4 h-4" />
              <span className="hidden sm:inline">الرسائل</span>
              <span className="sm:hidden">رسائل</span>
            </TabsTrigger>
            <TabsTrigger value="notifications" className="gap-1 text-xs sm:text-sm">
              <Bell className="w-4 h-4" />
              <span className="hidden sm:inline">الإشعارات</span>
              <span className="sm:hidden">إشعارات</span>
              {unreadCount > 0 && (
                <Badge variant="destructive" className="text-[10px] px-1.5 py-0 mr-1">{unreadCount}</Badge>
              )}
            </TabsTrigger>
          </TabsList>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>

        <TabsContent value="overview">
          <ParentOverview
            childExams={childExams}
            childGames={childGames}
            todayAttendance={todayAttendance}
            selectedChild={selectedChild}
            overdueInvoices={overdueInvoices}
            latestMessage={latestMessage}
            aiSuggestion={null}
          />
        </TabsContent>

        <TabsContent value="exams">
          <ParentExamResults childExams={childExams} />
        </TabsContent>

        <TabsContent value="assignments">
          {profile?.nursery_id && (
            <ParentAssignments childIds={children.map((c: any) => c.id)} classIds={children.map((c: any) => c.class_id)} nurseryId={profile.nursery_id} />
          )}
        </TabsContent>

        <TabsContent value="games">
          <ParentGameResults childGames={childGames} />
        </TabsContent>

        <TabsContent value="attendance">
          {selectedChild && profile?.nursery_id && (
            <ParentAttendance childId={selectedChild} nurseryId={profile.nursery_id} />
          )}
        </TabsContent>

        <TabsContent value="schedule">
          {profile?.nursery_id && (
            <ParentSchedule classId={selectedChildData?.class_id || null} nurseryId={profile.nursery_id} />
          )}
        </TabsContent>

        <TabsContent value="weekly-schedule">
          <ParentWeeklySchedule
            classId={selectedChildData?.class_id || null}
            childName={selectedChildData?.full_name}
          />
        </TabsContent>

        <TabsContent value="invoices">
          <ParentInvoices childIds={children.map((c: any) => c.id)} />
        </TabsContent>

        <TabsContent value="leaves">
          {profile?.nursery_id && (
            <ParentLeaveRequests children={children.map((c: any) => ({ id: c.id, full_name: c.full_name }))} nurseryId={profile.nursery_id} />
          )}
        </TabsContent>

        <TabsContent value="messages">
          {profile?.nursery_id && (
            <ParentMessaging nurseryId={profile.nursery_id} />
          )}
        </TabsContent>

        <TabsContent value="notifications">
          <Card className="shadow-card">
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2">
                <Bell className="w-4 h-4 text-primary" />
                الإشعارات
              </CardTitle>
              <div className="flex items-center gap-2">
                <Select value={notifFilter} onValueChange={setNotifFilter}>
                  <SelectTrigger className="h-8 w-[120px] text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">الكل</SelectItem>
                    <SelectItem value="educational">تعليمي</SelectItem>
                    <SelectItem value="administrative">إداري</SelectItem>
                    <SelectItem value="financial">مالي</SelectItem>
                  </SelectContent>
                </Select>
                {unreadCount > 0 && (
                  <Button variant="ghost" size="sm" className="text-xs h-8" onClick={markAllAsRead}>
                    تحديد الكل كمقروء
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {filteredNotifications.length === 0 ? (
                <div className="py-12 text-center">
                  <Bell className="w-10 h-10 text-muted-foreground/30 mx-auto mb-2" />
                  <p className="text-muted-foreground text-sm">لا توجد إشعارات</p>
                </div>
              ) : (
                <ScrollArea className="max-h-[400px]">
                  <div className="divide-y">
                    {filteredNotifications.map((n) => (
                      <div
                        key={n.id}
                        className={`p-4 flex items-start gap-3 cursor-pointer hover:bg-muted/30 transition-colors ${!n.is_read ? "bg-primary/5" : ""}`}
                        onClick={() => !n.is_read && markAsRead(n.id)}
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${!n.is_read ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}>
                          <Bell className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className={`text-sm font-medium ${!n.is_read ? "text-foreground" : "text-muted-foreground"}`}>{n.title}</p>
                            {!n.is_read && <div className="w-2 h-2 rounded-full bg-primary shrink-0" />}
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">{n.body}</p>
                          <p className="text-[10px] text-muted-foreground/60 mt-1">
                            {format(new Date(n.created_at), "dd MMM yyyy HH:mm", { locale: ar })}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="child-report">
          {selectedChild && profile?.nursery_id && selectedChildData && (
            <ParentChildReport
              childId={selectedChild}
              childName={selectedChildData.full_name}
              nurseryId={profile.nursery_id}
            />
          )}
        </TabsContent>
      </Tabs>

      {/* Floating Quick Message Button */}
      <motion.button
        className="fixed bottom-6 left-6 z-50 w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center hover:scale-105 transition-transform"
        whileTap={{ scale: 0.9 }}
        onClick={() => setQuickMsgOpen(true)}
        title="تواصل مع الإدارة"
      >
        <MessageSquare className="w-6 h-6" />
      </motion.button>

      {/* Quick Message Dialog with type selection */}
      <Dialog open={quickMsgOpen} onOpenChange={setQuickMsgOpen}>
        <DialogContent className="max-w-md" dir="rtl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-primary" />
              رسالة سريعة للإدارة
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Select value={quickMsgType} onValueChange={setQuickMsgType}>
              <SelectTrigger>
                <SelectValue placeholder="نوع الرسالة" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="inquiry">📩 استفسار</SelectItem>
                <SelectItem value="complaint">⚠️ شكوى</SelectItem>
                <SelectItem value="leave">🏖️ طلب إجازة</SelectItem>
              </SelectContent>
            </Select>
            <Textarea
              placeholder="اكتب رسالتك هنا..."
              value={quickMsg}
              onChange={(e) => setQuickMsg(e.target.value)}
              rows={4}
              className="resize-none"
            />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setQuickMsgOpen(false)}>إلغاء</Button>
              <Button onClick={sendQuickMessage} disabled={!quickMsg.trim() || sendingMsg} className="gap-1.5">
                {sendingMsg ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                إرسال
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ParentDashboard;
